import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Container,
  Grid,
  Button,
  Switch,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Divider,
  Typography,
  Box,
  Checkbox,
} from "@mui/material";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionIcon from "@mui/icons-material/Description";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import { FormControlBase, InputLabelSelect, SelectBase, TextFieldBase } from "../../Pedidos/styles";
import { BaseActionButton } from "../../Pedidos/components/actionButtons";
import { StyledTableCell } from "../../Pedidos/components/acesso/acessoIndex";

import api from "../../../Api/apiAxios";
import { formatTelefone, sanitizeEmail } from "../../../Utils/contato";
import { MyContext } from "../../../Context/MyContext";

function PermissaoItem({ title, checked, onToggle, icon, disabled = false }) {
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onToggle}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        px: 1.5,
        py: 0.76,
        borderRadius: 2,
        border: "1px solid",
        borderColor: disabled 
          ? "rgba(0, 0, 0, 0.12)" 
          : checked 
            ? "rgba(25, 118, 210, 0.55)" 
            : "rgba(0, 0, 0, 0.2)",
        bgcolor: disabled 
          ? "rgba(0, 0, 0, 0.02)" 
          : checked 
            ? "rgba(25, 118, 210, 0.06)" 
            : "background.paper",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all .15s ease",
        outline: "none",
        opacity: disabled ? 0.5 : 1,
        "&:hover": disabled ? {} : {
          bgcolor: checked ? "rgba(25, 118, 210, 0.10)" : "action.hover",
          borderColor: checked ? "rgba(25, 118, 210, 0.75)" : "divider",
        },
        "&:focus-visible": disabled ? {} : {
          boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}22`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            bgcolor: disabled 
              ? "action.disabledBackground" 
              : checked 
                ? "primary.main" 
                : "action.hover",
            color: disabled 
              ? "action.disabled" 
              : checked 
                ? "primary.contrastText" 
                : "text.secondary",
            transition: "all .15s ease",
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: disabled 
              ? "text.disabled" 
              : checked 
                ? "primary.main" 
                : "text.primary",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Checkbox
        checked={!!checked}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          if (disabled) return;
          e.stopPropagation();
          onToggle();
        }}
        sx={{
          p: 0.25,
          "& .MuiSvgIcon-root": { fontSize: 22 },
        }}
      />
    </Box>
  );
}

export function AdicionarUsuario({ distribuidorId = 0, clienteId = 0, statusRevenda = false }) {
  const [usuarios, setUsuarios] = useState([]);
  const [novoUsuario, setNovoUsuario] = useState(null);
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const nivelUsuario = window.localStorage.getItem("nivelUsuario");
  const { setMensagemGlobal, setTipoMensagemGlobal } = useContext(MyContext);

  const modo = useMemo(() => {
    const dId = Number(distribuidorId || 0);
    const cId = Number(clienteId || 0);

    if (dId > 0 && cId === 0) return "distribuidor";
    if (cId > 0 && dId === 0) return "cliente";

    if (dId > 0) return "distribuidor";
    if (cId > 0) return "cliente";
    return "nenhum";
  }, [distribuidorId, clienteId]);

  useEffect(() => {
    async function carregarModulosCliente() {
      if (modo !== "cliente") {
        setModulosCliente({
          temPonto: true,
          temFolha: true,
          temAcesso: true,
        });
        return;
      }

      const cId = Number(clienteId || 0);
      if (!cId) {
        setModulosCliente({
          temPonto: false,
          temFolha: false,
          temAcesso: false,
        });
        return;
      }

      try {
        const [licPonto, licAcesso, licFolha] = await Promise.all([
          api.get(`/LicencaPonto/cliente/${cId}`).catch(() => ({ data: [] })),
          api.get(`/LicencaAcesso/cliente/${cId}`).catch(() => ({ data: [] })),
          api.get(`/LicencaFolha/cliente/${cId}`).catch(() => ({ data: [] })),
        ]);

        const temLicencaValida = (lista, tipo) => {
          if (!lista) return false;
          
          const normalizarLista = Array.isArray(lista) ? lista : [lista];
          if (normalizarLista.length === 0) return false;
          
          return normalizarLista.some((lic) => {
            if (!lic || typeof lic !== "object") return false;
            
            const numeroSerie = lic.numeroSerie ?? lic.NumeroSerie ?? 0;
            const versao = lic.versao ?? lic.Versao ?? "";

            if (tipo === "ponto" || tipo === "acesso") {
              return numeroSerie > 0 || !!versao;
            }

            if (tipo === "folha") {
              return numeroSerie > 0;
            }

            return numeroSerie > 0 || !!versao;
          });
        };

        setModulosCliente({
          temPonto: temLicencaValida(licPonto.data, "ponto"),
          temFolha: temLicencaValida(licFolha.data, "folha"),
          temAcesso: temLicencaValida(licAcesso.data, "acesso"),
        });
      } catch {
        setModulosCliente({
          temPonto: false,
          temFolha: false,
          temAcesso: false,
        });
      }
    }

    carregarModulosCliente();
  }, [modo, clienteId]);

  const podeCadastrar = useMemo(() => {
    const temPermissaoNivel = nivelUsuario === "1" || nivelUsuario === "3";
    if (!temPermissaoNivel) return false;

    if (modo === "distribuidor") return !!statusRevenda;
    if (modo === "cliente") return true;

    return false;
  }, [modo, statusRevenda, nivelUsuario]);

  const tipoUsuarioLabel = {
    1: "Admin - BMA",
    2: "Consulta - BMA",
    3: "Admin - Revenda",
    4: "Consulta - Revenda",
    5: "Suporte - BMA",
    6: "Suporte - Revenda/Cliente",
  };

  const defaultPermissoes = {
    acessarDownloads: false,
    acessarChamados: false,
    moduloPonto: false,
    moduloFolha: false,
    moduloAcesso: false,
  };

  const [modulosCliente, setModulosCliente] = useState({
    temPonto: true,
    temFolha: true,
    temAcesso: true,
  });

  const fecharForm = () => {
    setNovoUsuario(null);
    setEditandoUsuario(null);
    setShowPassword(false);
  };

  const loadUsuarios = useCallback(async () => {
    try {
      if (modo === "distribuidor") {
        const resp = await api.get(`/Usuario/retornar-por-distribuidor?idDistribuidor=${Number(distribuidorId)}`);
        setUsuarios(resp.data);
        return;
      }

      if (modo === "cliente") {
        const resp = await api.get(`/Usuario/retornar-por-cliente?idCliente=${Number(clienteId)}`);
        setUsuarios(resp.data);
        return;
      }

      setUsuarios([]);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      setUsuarios([]);
    }
  }, [modo, distribuidorId, clienteId]);

  useEffect(() => {
    loadUsuarios();
  }, [loadUsuarios]);

  const handleAddUsuario = () => {
    const statusInicial = modo === "distribuidor" && !statusRevenda ? 0 : 1;
    
    setNovoUsuario({
      distribuidorId: modo === "distribuidor" ? Number(distribuidorId) : 0,
      clienteId: modo === "cliente" ? Number(clienteId) : 0,
      nome: "",
      email: "",
      celular: "",
      senha: "portal123",
      tipoUsuario: null,
      status: statusInicial,
      permissoes: { ...defaultPermissoes },
    });
    setShowPassword(false);
  };

  const handleEditUsuario = async (usuario) => {
    try {
      const { data: usuarioData } = await api.get(`/Usuario/${usuario.id}`);

      let permissoes = { ...defaultPermissoes };
      try {
        const respPerm = await api.get(`/RestritoUsuarioPermissoes/usuario/${usuario.id}`);
        if (respPerm?.data?.usuarioId) {
          permissoes = {
            acessarDownloads: !!respPerm.data.acessarDownloads,
            acessarChamados: !!respPerm.data.acessarChamados,
            moduloPonto: !!respPerm.data.moduloPonto,
            moduloFolha: !!respPerm.data.moduloFolha,
            moduloAcesso: !!respPerm.data.moduloAcesso,
          };
        }
      } catch {
      }

      setEditandoUsuario(usuario);
      
      let statusUsuario = usuarioData.status;
      if (modo === "distribuidor" && !statusRevenda && statusUsuario === 1) {
        statusUsuario = 0;
      }
      
      setNovoUsuario({
        ...usuarioData,
        celular: formatTelefone(usuarioData.celular),
        senha: "*******",
        distribuidorId: modo === "distribuidor" ? Number(distribuidorId) : 0,
        clienteId: modo === "cliente" ? Number(clienteId) : 0,
        tipoUsuario: usuarioData.tipoUsuario || usuario.tipoUsuario,
        status: statusUsuario,
        permissoes,
      });
      setShowPassword(false);
    } catch (e) {
      console.error(e);
      setMensagemGlobal("Erro ao carregar dados do usuário para edição.");
      setTipoMensagemGlobal("danger");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const masks = { email: sanitizeEmail, celular: formatTelefone };
    const formatted = masks[name] ? masks[name](value) : value;
    setNovoUsuario((prev) => ({ ...prev, [name]: formatted }));
  };

  const togglePermissao = (key) => {
    setNovoUsuario((prev) => ({
      ...prev,
      permissoes: {
        ...(prev?.permissoes ?? defaultPermissoes),
        [key]: !prev?.permissoes?.[key],
      },
    }));
  };

  const marcarTodas = () => {
    setNovoUsuario((prev) => ({
      ...prev,
      permissoes: {
        acessarDownloads: true,
        acessarChamados: true,
        moduloPonto: true,
        moduloFolha: true,
        moduloAcesso: true,
      },
    }));
  };

  const limparPermissoes = () => {
    setNovoUsuario((prev) => ({
      ...prev,
      permissoes: { ...defaultPermissoes },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    try {
      setIsSaving(true);

      if (!novoUsuario?.nome || !novoUsuario?.email || !novoUsuario?.tipoUsuario) {
        setMensagemGlobal("Preencha os campos obrigatórios: Nome, Email e Tipo de Usuário.");
        setTipoMensagemGlobal("danger");
        return;
      }

      if (!editandoUsuario && !novoUsuario?.senha) {
        setMensagemGlobal("Preencha o campo Senha para novo usuário.");
        setTipoMensagemGlobal("danger");
        return;
      }

      if (modo === "distribuidor" && !(Number(novoUsuario.distribuidorId) > 0)) {
        setMensagemGlobal("DistribuidorId inválido para contato de revenda.");
        setTipoMensagemGlobal("danger");
        return;
      }

      if (modo === "cliente" && !(Number(novoUsuario.clienteId) > 0)) {
        setMensagemGlobal("ClienteId inválido para contato de cliente.");
        setTipoMensagemGlobal("danger");
        return;
      }

      if (modo === "distribuidor" && !statusRevenda && novoUsuario.status === 1) {
        setMensagemGlobal("Não é possível ativar um usuário enquanto a revenda estiver inativa.");
        setTipoMensagemGlobal("danger");
        return;
      }

      const { data: retorno } = await api.get("/Usuario/validar-email", {
        params: { email: novoUsuario.email },
      });

      const conflitaComUsuarioAtivo =
        retorno.id > 0 &&
        retorno.status === 1 &&
        (!editandoUsuario || (novoUsuario.status === 1 && retorno.id !== Number(novoUsuario.id)));

      if (conflitaComUsuarioAtivo) {
        setMensagemGlobal("E-mail já cadastrado, favor informar outro e-mail!");
        setTipoMensagemGlobal("danger");
        return;
      }

      const regUsuario = {
        Id: editandoUsuario?.id || 0,
        DistribuidorId: modo === "distribuidor" ? Number(novoUsuario.distribuidorId) : 0,
        ClienteId: modo === "cliente" ? Number(novoUsuario.clienteId) : 0,
        Nome: novoUsuario.nome,
        Email: novoUsuario.email,
        Senha: editandoUsuario ? "" : novoUsuario.senha,
        Telefone: novoUsuario.celular || "",
        Celular: novoUsuario.celular || "",
        Status: novoUsuario.status,
        TipoUsuario: Number(novoUsuario.tipoUsuario),
        DataHoraUltimoAcesso: new Date(),
        EnviarEmailCredenciais: 0,
        EnviarEmailFaturamento: 0,
      };

      if (!editandoUsuario) {
        regUsuario.DataHoraCadastro = new Date();
      }

      let userIdSalvo = regUsuario.Id;

      if (editandoUsuario) {
        await api.put("Usuario/", JSON.stringify(regUsuario), {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        });
      } else {
        await api.post("Usuario/", JSON.stringify(regUsuario), {
          headers: { "Content-Type": "application/json" },
        });

        const u = await api.get(`/Usuario/validar-email?email=${encodeURIComponent(regUsuario.Email)}`);
        userIdSalvo = u?.data?.id || 0;
      }

      if (!userIdSalvo) {
        setMensagemGlobal("Usuário salvo, mas não foi possível identificar o Id para salvar permissões.");
        setTipoMensagemGlobal("danger");
        return;
      }

      const permissoesPayload = {
        UsuarioId: Number(userIdSalvo),
        AcessarDownloads: !!novoUsuario.permissoes?.acessarDownloads,
        AcessarChamados: !!novoUsuario.permissoes?.acessarChamados,
        ModuloPonto: !!novoUsuario.permissoes?.moduloPonto,
        ModuloFolha: !!novoUsuario.permissoes?.moduloFolha,
        ModuloAcesso: !!novoUsuario.permissoes?.moduloAcesso,
      };

      if (
        (permissoesPayload.AcessarDownloads || permissoesPayload.AcessarChamados) &&
        !(
          permissoesPayload.ModuloPonto ||
          permissoesPayload.ModuloFolha ||
          permissoesPayload.ModuloAcesso
        )
      ) {
        setMensagemGlobal(
          "Ao marcar 'Downloads' ou 'Chamados', é obrigatório selecionar pelo menos um módulo (Ponto, Acesso ou Folha)."
        );
        setTipoMensagemGlobal("danger");
        return;
      }

      const isSuporte =
        novoUsuario.tipoUsuario === 5 || novoUsuario.tipoUsuario === 6;

      if (
        isSuporte &&
        !(
          permissoesPayload.AcessarDownloads ||
          permissoesPayload.AcessarChamados ||
          permissoesPayload.ModuloPonto ||
          permissoesPayload.ModuloFolha ||
          permissoesPayload.ModuloAcesso
        )
      ) {
        setMensagemGlobal(
          "Para usuários do tipo Suporte é obrigatório marcar pelo menos uma permissão do restrito."
        );
        setTipoMensagemGlobal("danger");
        return;
      }

      await api.put(`RestritoUsuarioPermissoes/usuario/${userIdSalvo}`, JSON.stringify(permissoesPayload), {
        headers: { "Content-Type": "application/json" },
      });

      await loadUsuarios();
      fecharForm();
      setMensagemGlobal(editandoUsuario ? "Contato atualizado com sucesso." : "Contato cadastrado com sucesso.");
      setTipoMensagemGlobal("success");
    } catch (error) {
      console.error("Erro ao salvar usuário/permissões:", error);
      console.error("Response data:", error?.response?.data);
      console.error("Request data:", error?.config?.data);
      
      const payload = error?.response?.data;
      let msg = "Erro ao salvar usuário/permissões.";
      
      if (payload) {
        if (typeof payload === "string") {
          msg = payload;
        } else if (payload.title) {
          if (payload.title === "One or more validation errors occurred.") {
            msg = "Erro de validação. Verifique os campos obrigatórios.";
          } else {
            msg = payload.title;
          }
          if (payload.errors && Object.keys(payload.errors).length > 0) {
            const errorDetails = Object.entries(payload.errors)
              .map(([key, value]) => {
                const fieldName = key === "Senha" ? "Senha" : key;
                const errorMsg = Array.isArray(value) ? value[0] : value;
                if (errorMsg.includes("required")) {
                  return `${fieldName}: campo obrigatório`;
                }
                return `${fieldName}: ${errorMsg}`;
              })
              .join(" | ");
            msg = `${msg} ${errorDetails}`;
          }
        } else if (payload.errors) {
          const errorMessages = Object.entries(payload.errors)
            .map(([key, value]) => {
              const fieldName = key === "Senha" ? "Senha" : key;
              const errorMsg = Array.isArray(value) ? value[0] : value;
              if (errorMsg.includes("required")) {
                return `${fieldName}: campo obrigatório`;
              }
              return `${fieldName}: ${errorMsg}`;
            })
            .join(" | ");
          msg = errorMessages || msg;
        } else if (payload.message) {
          msg = payload.message;
        }
      } else if (error.message) {
        msg = error.message;
      }

      setMensagemGlobal(msg);
      setTipoMensagemGlobal("danger");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container
      sx={{
        my: 2,
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      }}
    >
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Nome</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Celular</StyledTableCell>
              <StyledTableCell>Tipo</StyledTableCell>
              <StyledTableCell>Ativo</StyledTableCell>
              <StyledTableCell>Ações</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map((usuario, index) => (
              <TableRow key={index}>
                <StyledTableCell>{usuario.nome}</StyledTableCell>
                <StyledTableCell>{usuario.email}</StyledTableCell>
                <StyledTableCell>{usuario.celular}</StyledTableCell>
                <StyledTableCell>{tipoUsuarioLabel[usuario.tipoUsuario]}</StyledTableCell>
                <StyledTableCell>
                  <Switch checked={usuario.status === 1} disabled />
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Editar">
                      <BaseActionButton
                        color="#ffc107"
                        onClick={() => handleEditUsuario(usuario)}
                        disabled={nivelUsuario === "2" || nivelUsuario === "4" || novoUsuario !== null}
                      >
                        <EditIcon />
                      </BaseActionButton>
                    </Tooltip>
                    {nivelUsuario === "1" && (
                      <Tooltip title="Enviar credenciais por e-mail">
                        <BaseActionButton
                          color="#6c757d"
                          onClick={async () => {
                            try {
                              setSendingEmail(true);
                              const response = await api.get(`/Usuario/recuperacao-credenciais?id=${usuario.id}`);
                              setMensagemGlobal(
                                response.data || "E-mail de recuperação de credenciais enviado com sucesso."
                              );
                              setTipoMensagemGlobal("success");
                            } catch (error) {
                              console.error("Erro ao enviar credenciais:", error);
                              const errorMsg =
                                error?.response?.data || "Erro ao enviar e-mail de recuperação de credenciais.";
                              setMensagemGlobal(errorMsg);
                              setTipoMensagemGlobal("danger");
                            } finally {
                              setSendingEmail(false);
                            }
                          }}
                          disabled={usuario.status !== 1 || novoUsuario !== null || sendingEmail}
                        >
                          <EmailIcon />
                        </BaseActionButton>
                      </Tooltip>
                    )}
                  </Box>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <br />

      {podeCadastrar && (
        <Button
          onClick={handleAddUsuario}
          variant="text"
          sx={{ display: "flex", alignItems: "center" }}
          disabled={novoUsuario !== null}
        >
          <AddCircleIcon sx={{ marginRight: "0.5rem" }} />
          Novo Contato
        </Button>
      )}

      {novoUsuario && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 3, py: 2.25, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontWeight: 900, fontSize: 22, lineHeight: 1.15 }}>
              {editandoUsuario ? "Editar contato" : "Novo contato"}
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} order={{ xs: 1 }} sx={{ textAlign: "start" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start"}}>
                  <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>
                    Dados do Usuário
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2.2 }}>
                    Preencha os dados básicos do contato
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <TextFieldBase
                      label="Nome completo"
                      name="nome"
                      placeholder="Digite o nome"
                      value={novoUsuario.nome}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <TextFieldBase
                      label="E-mail"
                      name="email"
                      type="email"
                      placeholder="email@exemplo.com"
                      value={novoUsuario.email}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <TextFieldBase
                      label="Celular"
                      name="celular"
                      placeholder="(00) 00000-0000"
                      inputProps={{ inputMode: "tel", maxLength: 15 }}
                      value={novoUsuario.celular}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <TextFieldBase
                      label={editandoUsuario ? "Senha" : "Senha inicial"}
                      name="senha"
                      type={showPassword ? "text" : "password"}
                      value={novoUsuario.senha}
                      onChange={handleChange}
                      disabled={!!editandoUsuario}
                      fullWidth
                      InputProps={{
                        endAdornment: !editandoUsuario && (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <FormControlBase>
                      <InputLabelSelect>Tipo de usuário</InputLabelSelect>

                      <SelectBase
                        name="tipoUsuario"
                        value={novoUsuario.tipoUsuario ?? ""}
                        displayEmpty
                        onChange={(e) =>
                          setNovoUsuario((prev) => ({
                            ...prev,
                            tipoUsuario: Number(e.target.value) || null,
                          }))
                        }
                        fullWidth
                        renderValue={(selected) => {
                          if (selected === "") return "Selecione...";
                          return tipoUsuarioLabel[selected];
                        }}
                      >
                        <MenuItem value="" disabled>
                          Selecione...
                        </MenuItem>

                        {nivelUsuario === "1" && <MenuItem value={1}>Admin - BMA</MenuItem>}
                        {nivelUsuario === "1" && <MenuItem value={2}>Consulta - BMA</MenuItem>}
                        {nivelUsuario === "1" && <MenuItem value={5}>Suporte - BMA</MenuItem>}
                        <MenuItem value={3}>Admin - Revenda</MenuItem>
                        <MenuItem value={4}>Consulta - Revenda</MenuItem>
                        <MenuItem value={6}>Suporte - Revenda/Cliente</MenuItem>
                      </SelectBase>
                    </FormControlBase>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip
                      title={
                        modo === "distribuidor" && !statusRevenda
                          ? "Não é possível ativar um usuário enquanto a revenda estiver inativa"
                          : ""
                      }
                    >
                      <span>
                        <Switch
                          checked={novoUsuario.status === 1}
                          onChange={(e) => {
                            if (modo === "distribuidor" && !statusRevenda && e.target.checked) {
                              setMensagemGlobal("Não é possível ativar um usuário enquanto a revenda estiver inativa.");
                              setTipoMensagemGlobal("danger");
                              return;
                            }
                            setNovoUsuario((prev) => ({ ...prev, status: e.target.checked ? 1 : 0 }));
                          }}
                          disabled={modo === "distribuidor" && !statusRevenda}
                          size="small"
                        />
                      </span>
                    </Tooltip>
                    <Typography sx={{ fontWeight: 700 }}>Usuário ativo</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6} order={{ xs: 2 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "left" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2, textAlign: "left" }}>
                      Permissões do Restrito
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, textAlign: "left" }}>
                      Escolha quais áreas o usuário poderá acessar
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={limparPermissoes} disabled={isSaving}>
                      Limpar
                    </Button>
                    <Button size="small" variant="outlined" onClick={marcarTodas} disabled={isSaving}>
                      Marcar todas
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.8 }}>
                  <PermissaoItem
                    title="Downloads"
                    icon={<DownloadIcon fontSize="small" />}
                    checked={novoUsuario.permissoes?.acessarDownloads}
                    onToggle={() => togglePermissao("acessarDownloads")}
                  />
                  <PermissaoItem
                    title="Chamados"
                    icon={<SupportAgentIcon fontSize="small" />}
                    checked={novoUsuario.permissoes?.acessarChamados}
                    onToggle={() => togglePermissao("acessarChamados")}
                  />
                  <PermissaoItem
                    title="Módulo Ponto"
                    icon={<AccessTimeIcon fontSize="small" />}
                    checked={novoUsuario.permissoes?.moduloPonto}
                    onToggle={() => {
                      if (modo === "cliente" && !modulosCliente.temPonto) return;
                      togglePermissao("moduloPonto");
                    }}
                    disabled={modo === "cliente" && !modulosCliente.temPonto}
                  />
                  <PermissaoItem
                    title="Módulo Folha"
                    icon={<DescriptionIcon fontSize="small" />}
                    checked={novoUsuario.permissoes?.moduloFolha}
                    onToggle={() => {
                      if (modo === "cliente" && !modulosCliente.temFolha) return;
                      togglePermissao("moduloFolha");
                    }}
                    disabled={modo === "cliente" && !modulosCliente.temFolha}
                  />
                  <PermissaoItem
                    title="Módulo Acesso"
                    icon={<VpnKeyIcon fontSize="small" />}
                    checked={novoUsuario.permissoes?.moduloAcesso}
                    onToggle={() => {
                      if (modo === "cliente" && !modulosCliente.temAcesso) return;
                      togglePermissao("moduloAcesso");
                    }}
                    disabled={modo === "cliente" && !modulosCliente.temAcesso}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} order={{ xs: 3}}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                  <Button variant="outlined" color="secondary" onClick={fecharForm} disabled={isSaving}>
                    Cancelar
                  </Button>
                  <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Confirmar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}
    </Container>
  );
}
